import { Mail } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface EmailVerificationGateProps {
  email: string;
  onResend: () => Promise<unknown>;
}

export function EmailVerificationGate({
  email,
  onResend,
}: EmailVerificationGateProps): ReactElement {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { logout } = useAuth();

  const handleResend = async (): Promise<void> => {
    setSending(true);
    try {
      await onResend();
      setSent(true);
    } catch {
      // silently fail
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-dvh bg-theme-bg flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md bg-theme-surface rounded-xl border border-theme-border p-8 text-center">
        <div className="w-16 h-16 bg-theme-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-theme-warning" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Email Verification Required</h2>
        <p className="text-theme-text-secondary mb-2">
          Your sign-in provider couldn&apos;t verify your email address.
        </p>
        <p className="text-theme-text-secondary mb-6">
          We sent a verification link to <span className="text-white font-medium">{email}</span>.
          Check your inbox and click the link to access your account.
        </p>

        {sent && <p className="text-theme-success text-sm mb-4">Verification email sent!</p>}

        <button
          type="button"
          onClick={handleResend}
          disabled={sending}
          className="w-full bg-theme-primary-fill hover:bg-theme-primary-fill/80 text-theme-on-primary-fill font-medium py-2.5 px-4 rounded-lg transition-colors mb-3 disabled:opacity-50"
        >
          {sending ? 'Sending...' : 'Resend Verification Email'}
        </button>

        <button
          type="button"
          onClick={() => logout()}
          className="w-full text-theme-text-tertiary hover:text-theme-text-secondary text-sm transition-colors"
        >
          Sign out and use a different account
        </button>
      </div>
    </div>
  );
}
